import { useState } from 'react'
import { Briefcase, Coins, ShieldCheck, X } from 'lucide-react'
import {
  ASSIGNABLE_JOB_TITLES,
  ASSIGNABLE_ROYALTY_ROLES,
  JOB_TITLE_LABELS,
  ROYALTY_ROLE_HINTS,
  ROYALTY_ROLE_LABELS,
  assignJobTitle,
  clearJobTitle,
  jobTitleBlockReason,
  setManagementRoyaltyRole,
  setManagementSuperAdmin,
  type AssignableJobTitle,
} from '@/services/managementRolesService'
import type { ManagementUser } from '@/services/managementUsersService'
import type { RoyaltyRole } from '@/types'

/**
 * Роли сотрудника УК: должность, роль в роялти, супер-админ.
 *
 * Секции применяются по отдельности — у каждой оси свой серверный гейт и свои гарды,
 * поэтому общая кнопка «Сохранить» либо врала бы про частичный успех, либо требовала
 * транзакции, которой в PostgREST нет.
 */

type ActorCapabilities = {
  /** Может выдавать должности ТУ / ЛМ / педагогов (серверные гейты `*_require_advanced`). */
  isAdvanced: boolean
  /** Юрист и супер-админ — только сама роль супер-админа. */
  isSuperAdmin: boolean
  /** Роль в роялти меняют только `head` и `super_admin` контура роялти. */
  royaltyRole: RoyaltyRole | null
}

function isAssignable(title: ManagementUser['job_title']): title is AssignableJobTitle {
  return title !== null && title !== 'other'
}

function SectionResult({ error, notice }: { error: string | null; notice: string | null }) {
  if (!error && !notice) return null
  return (
    <div
      className={`mt-2 rounded border px-2 py-1.5 text-[11px] ${
        error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      }`}
    >
      {error ?? notice}
    </div>
  )
}

export function UserRolesDialog({
  user,
  actor,
  isSelf,
  royaltyRolesAvailable,
  superAdminFlagsAvailable,
  onClose,
  onChanged,
}: {
  user: ManagementUser
  actor: ActorCapabilities
  isSelf: boolean
  royaltyRolesAvailable: boolean
  /** Не загрузились — текущее состояние признака неизвестно, менять его вслепую нельзя. */
  superAdminFlagsAvailable: boolean
  onClose: () => void
  onChanged: () => Promise<unknown> | void
}) {
  const [jobBusy, setJobBusy] = useState(false)
  const [jobError, setJobError] = useState<string | null>(null)
  const [jobNotice, setJobNotice] = useState<string | null>(null)

  const [royaltyRole, setRoyaltyRole] = useState<RoyaltyRole | ''>(user.royalty_role ?? '')
  const [royaltyBusy, setRoyaltyBusy] = useState(false)
  const [royaltyError, setRoyaltyError] = useState<string | null>(null)
  const [royaltyNotice, setRoyaltyNotice] = useState<string | null>(null)

  const [superBusy, setSuperBusy] = useState(false)
  const [superError, setSuperError] = useState<string | null>(null)
  const [superNotice, setSuperNotice] = useState<string | null>(null)

  const memberOfMc = user.mc_is_active && user.access_level !== null
  const currentTitle = user.job_title

  const run = async (
    action: () => Promise<void>,
    setBusy: (value: boolean) => void,
    setError: (value: string | null) => void,
    setNotice: (value: string | null) => void,
    successMessage: string
  ) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await action()
      await onChanged()
      setNotice(successMessage)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Не удалось сохранить')
    } finally {
      setBusy(false)
    }
  }

  /** Причина, по которой действие недоступно текущему администратору. */
  const actorBlockForTitle = (title: AssignableJobTitle): string | null => {
    if (title === 'lawyer') {
      return actor.isSuperAdmin ? null : 'Роль юриста выдаёт только супер-администратор.'
    }
    return actor.isAdvanced ? null : 'Должности выдаёт сотрудник УК с расширенным доступом.'
  }

  const royaltyActorBlock = !royaltyRolesAvailable
    ? 'Роли в роялти не загрузились: нужен расширенный доступ УК или роль в контуре роялти.'
    : actor.royaltyRole === 'head' || actor.royaltyRole === 'super_admin'
      ? null
      : 'Менять роли в роялти могут только руководитель и администратор контура роялти.'

  const royaltyTargetBlock = memberOfMc ? null : 'Роль в роялти выдаётся только активному сотруднику УК.'

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Роли и должность</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {[user.first_name, user.last_name].filter(Boolean).join(' ') ||
                user.email ||
                user.tg_username ||
                'Без имени'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!memberOfMc && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            У пользователя нет активного доступа в УК. Должности и роли выдаются только действующим
            сотрудникам управляющей компании — сначала выдайте доступ.
          </div>
        )}

        <section className="mt-4 rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Должность</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Должность в УК одна на человека: чтобы выдать новую, сначала снимите текущую — иначе
            права и закрепления прежней роли снялись бы молча.
          </p>

          <div className="mt-2 text-xs text-foreground">
            Сейчас:{' '}
            <span className="font-medium">
              {currentTitle ? JOB_TITLE_LABELS[currentTitle] : 'без должности'}
            </span>
          </div>

          {currentTitle === 'other' && (
            <div className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
              Должность «Другое» этим экраном не снимается: серверной ручки под неё нет.
            </div>
          )}

          {isAssignable(currentTitle) ? (
            <button
              type="button"
              disabled={jobBusy || Boolean(actorBlockForTitle(currentTitle))}
              title={actorBlockForTitle(currentTitle) ?? undefined}
              onClick={() =>
                void run(
                  () => clearJobTitle({ userId: user.user_id, current: currentTitle }),
                  setJobBusy,
                  setJobError,
                  setJobNotice,
                  `Должность «${JOB_TITLE_LABELS[currentTitle]}» снята`
                )
              }
              className="mt-3 h-9 rounded-md border border-border px-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {jobBusy ? 'Сохраняем…' : `Снять должность «${JOB_TITLE_LABELS[currentTitle]}»`}
            </button>
          ) : (
            <div className="mt-3 grid gap-1.5">
              {ASSIGNABLE_JOB_TITLES.map(title => {
                const block = actorBlockForTitle(title) ?? jobTitleBlockReason(title, user)
                return (
                  <button
                    key={title}
                    type="button"
                    disabled={jobBusy || Boolean(block)}
                    title={block ?? undefined}
                    onClick={() =>
                      void run(
                        () => assignJobTitle({ userId: user.user_id, title }),
                        setJobBusy,
                        setJobError,
                        setJobNotice,
                        `Должность «${JOB_TITLE_LABELS[title]}» выдана`
                      )
                    }
                    className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left text-sm text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span>{JOB_TITLE_LABELS[title]}</span>
                    {block && <span className="text-[11px] text-muted-foreground">{block}</span>}
                  </button>
                )
              })}
            </div>
          )}

          <SectionResult error={jobError} notice={jobNotice} />
        </section>

        <section className="mt-3 rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Роль в роялти</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Ось независимая от уровня доступа УК: бухгалтером роялти может быть сотрудник с обычным
            доступом, а расширенный доступ сам по себе прав в роялти не даёт.
          </p>

          <select
            value={royaltyRole}
            onChange={event => {
              setRoyaltyRole(event.target.value as RoyaltyRole | '')
              setRoyaltyError(null)
              setRoyaltyNotice(null)
            }}
            disabled={royaltyBusy || Boolean(royaltyActorBlock) || Boolean(royaltyTargetBlock)}
            className="mt-2 h-9 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-slate-500 disabled:opacity-50"
          >
            <option value="">Без роли в роялти</option>
            {ASSIGNABLE_ROYALTY_ROLES.map(role => (
              <option key={role} value={role}>
                {ROYALTY_ROLE_LABELS[role]}
              </option>
            ))}
          </select>

          {royaltyRole && (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{ROYALTY_ROLE_HINTS[royaltyRole]}</p>
          )}
          {user.royalty_role === 'territorial_manager' && (
            <p className="mt-1.5 text-[11px] text-amber-700">
              Текущая роль — «{ROYALTY_ROLE_LABELS.territorial_manager}»; выдать её заново этим
              экраном нельзя, серверная ручка её не принимает.
            </p>
          )}
          {(royaltyActorBlock || royaltyTargetBlock) && (
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {royaltyActorBlock ?? royaltyTargetBlock}
            </p>
          )}

          <button
            type="button"
            disabled={
              royaltyBusy ||
              Boolean(royaltyActorBlock) ||
              Boolean(royaltyTargetBlock) ||
              (royaltyRole || null) === (user.royalty_role ?? null)
            }
            onClick={() =>
              void run(
                () => setManagementRoyaltyRole({ userId: user.user_id, role: royaltyRole || null }),
                setRoyaltyBusy,
                setRoyaltyError,
                setRoyaltyNotice,
                royaltyRole ? `Роль в роялти: ${ROYALTY_ROLE_LABELS[royaltyRole]}` : 'Роль в роялти снята'
              )
            }
            className="mt-2 h-9 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {royaltyBusy ? 'Сохраняем…' : 'Сохранить роль в роялти'}
          </button>

          <SectionResult error={royaltyError} notice={royaltyNotice} />
        </section>

        <section className="mt-3 rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Супер-администратор</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Даёт этот раздел целиком: справочник пользователей с контактами, журнал входов, выдачу
            доступа в УК и роль юриста.
          </p>
          <div className="mt-2 text-xs text-foreground">
            Сейчас:{' '}
            <span className="font-medium">
              {superAdminFlagsAvailable ? (user.is_super_admin ? 'да' : 'нет') : 'не загрузилось'}
            </span>
          </div>

          <button
            type="button"
            disabled={superBusy || !actor.isSuperAdmin || !user.mc_is_active || !superAdminFlagsAvailable}
            title={
              !actor.isSuperAdmin
                ? 'Роль супер-администратора выдаёт только сама роль'
                : !superAdminFlagsAvailable
                  ? 'Текущее состояние признака не загрузилось — обновите список'
                  : !user.mc_is_active
                    ? 'Нужен активный доступ в УК'
                    : undefined
            }
            onClick={() => {
              const next = !user.is_super_admin
              const question = next
                ? 'Выдать роль супер-администратора? Человек получит доступ к контактам всех пользователей и к выдаче доступов в УК.'
                : isSelf
                  ? 'Снять роль супер-администратора с себя? Раздел «Пользователи» закроется сразу после сохранения.'
                  : 'Снять роль супер-администратора?'
              if (!confirm(question)) return
              void run(
                () => setManagementSuperAdmin({ userId: user.user_id, isSuperAdmin: next }),
                setSuperBusy,
                setSuperError,
                setSuperNotice,
                next ? 'Роль супер-администратора выдана' : 'Роль супер-администратора снята'
              )
            }}
            className={`mt-2 h-9 rounded-md px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
              user.is_super_admin
                ? 'border border-border text-red-600 hover:bg-red-50'
                : 'bg-fixed-emerald-600 text-white hover:bg-fixed-emerald-700'
            }`}
          >
            {superBusy
              ? 'Сохраняем…'
              : user.is_super_admin
                ? 'Снять роль супер-администратора'
                : 'Выдать роль супер-администратора'}
          </button>

          <SectionResult error={superError} notice={superNotice} />
        </section>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}
